import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, Button, Dialog, Link } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import DefaultScreen from '../DefaultScreen';
import useRedirect from '../../helpers/Routes/Redirect';
import { sitePrefix } from '../../config';
import { ClassesType } from '../Classes.types';
import { PulseemFeatures } from '../../model/PulseemFields/Fields';
import { eSendChannel, getChannelDescriptor } from '../../Models/DataSources/SmartSend';
import CampaignPicker, { CampaignRow, isSelectable, blockedReasonKey } from './components/CampaignPicker';
import InlineBanner from './components/InlineBanner';
import WhatsAppTemplatePicker, { templateBlockReason } from './whatsapp/WhatsAppTemplatePicker';
import SourcePicker from './components/SourcePicker';
import {
    getWaAvailability, getWaTemplates, setSuppressEmail, setWithWhatsApp, createWaContainer,
    resetWaIntentForCampaign, setWaUnit, resetWaForPicker, peekWaMapping, waUnitSummary, WaUnitSummary,
    selectWaTemplate, setWaFromNumber,
} from '../../redux/reducers/smartSendWhatsAppSlice';
import {
    selectWaAllowed, selectWaEnabled, selectWaActive, selectEmailOnIntent,
    activeChannelKeys, channelsFrom, WaRootState,
    selectSelectedSourceRow, sourceCanSend, missingIdentity, selectWaOnlyIntent,
} from './whatsapp/selectors';
import { WaPendingLeg, EMAIL_SENT_STATUSES } from '../../Models/DataSources/SmartSendWhatsApp';
import { resetForPicker, peekMapping, leaveSend } from '../../redux/reducers/smartSendSlice';
import { getDataSources } from '../../redux/reducers/dataSourcesSlice';
import { eDataSourceStatus } from '../../Models/DataSources/DataSource';
import { whatsappRoutes } from '../Whatsapp/Constant';
import {
    armConfirmReset, confirmResetTarget, isConfirmResetArmed, takeChangeSourceTrip, clearAllConfirmResets, markConfirmEntry,
} from './confirmReset';
import { sectionsView, sectionsInputFrom, ContinueGateInput, SectionsInput } from './pickerSections';
import {
    wizardView, wizardReconcile, chooseChannels, sourceGateChannels, reviewWarnings, acceptsClick, leaveFix, arrivalFocus,
    continueDecision, leaveOutcome, keepBothPath,
    ChannelAction, ChannelSet, GuardFacts, WizardInput, WizardStep, ReviewWarning, NotNeededReason,
} from './pickerWizard';
import {
    WizardStepper, StepCard, WizardNote, ChannelCards, ReviewList, WizardNav, ReviewRowView, WizardSkeleton, BusyLabel,
    useStableScrollbar,
} from './components/WizardParts';

// 🔴 09-channel-units — EVERY CHANNEL LEAVES THIS SCREEN WITH ITS OWN UNIT (06-docs §1.1).
// A unit is a container plus its own mapping row. The e-mail's container is the campaign the
// operator picks here (dbo.Campaigns); its row is written by the e-mail's own first save. The
// WhatsApp's container is CREATED here from the chosen template — `POST WhatsApp/Container`,
// carrying the source, so the server writes the unit's own mapping row (Channel 3) in the same
// request — whether WhatsApp goes alone or together with the e-mail. The mapping screen then only
// loads units that exist: `?channel=3` for WhatsApp alone, `?wa={unit}` beside the e-mail for both.
// ⚠️ There is no leg any more: WhatsApp never hangs on the e-mail campaign, so this screen reads no
// leg, lists no "pending legs" and offers no leg removal. A leg an older campaign may still carry
// is ignored — it is not a unit of any send made from here.

// FU-06A — a customer's name inside a translated (RTL) sentence, isolated with FSI…PDI (U+2068 … U+2069) so a
// Latin/numeric name is not reordered. Written as code points: invisible characters do not belong in the source.
const isolateBidi = (s: string): string => `${String.fromCharCode(0x2068)}${s}${String.fromCharCode(0x2069)}`;

// Smart-Send picker (§7). Route: `${sitePrefix}SmartSend` — no :id. Channel → content per channel
// (the e-mail campaign; the WhatsApp template) → source when the door brought none → continue to
// `${sitePrefix}Campaigns/SmartSend/:id`. The campaign list is fetched and owned by CampaignPicker.
// Everything WhatsApp is behind `AllowWhatsAppToAgent ∧ Availability.Enabled`; with either off,
// none of it renders and no WhatsApp/* call is made.

const SmartSendPicker = ({ classes }: ClassesType) => {
    const { t, i18n } = useTranslation();
    const Redirect = useRedirect();
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    // Entry A (§11.2): ?dataSourceId= is carried through to the mapping screen untouched — that
    // screen is the only consumer (and validates it there). The one thing this screen does with
    // it is the purely informational "sending from source" chip below.
    const dataSourceId = searchParams.get('dataSourceId');
    // 🔴 2026-09-23 · ONE ROAD IN. `?campaignId=` is the campaign-side twin of `?dataSourceId=`.
    // The campaign-management row action and the BEE editor's "שליחה ממקור" used to jump
    // straight to `Campaigns/SmartSend/:id`, skipping this screen — so the source was chosen on
    // the mapping screen for those two doors and here for the other three, and the two halves
    // of the flow drifted. Both now land HERE with the campaign already known, so every door
    // reaches the same screen in the same state and only the unknown half is asked for.
    // Sanitised the same way the mapping screen sanitises its own params (SmartSendScreen:75-76):
    // garbage is NOT a campaign, and must fall back to "nothing known" rather than to a bogus id.
    const campaignIdParam = searchParams.get('campaignId');
    const seededCampaignId = useMemo(() => {
        const n = campaignIdParam != null ? Number(campaignIdParam) : NaN;
        return !Number.isNaN(n) && n > 0 ? n : null;
    }, [campaignIdParam]);

    const { accountFeatures } = useSelector((state: any) => state.common);
    const dataSourceList = useSelector((s: any) => s.dataSources.list);
    // The source chosen ON THIS SCREEN when the caller did not bring one. SourcePicker writes it
    // to `smartSend.dataSourceId` through its own `selectSource`, and `navigate` reads it back
    // below — so a source settled here travels in the URL exactly like one that arrived in it.
    const pickedSourceId = useSelector((s: any) => s.smartSend.dataSource?.DataSourceID ?? s.smartSend.dataSourceId ?? null);
    const [campaign, setCampaign] = useState<CampaignRow | null>(null);
    const [pendingLeg, setPendingLeg] = useState<WaPendingLeg | null>(null);
    // Continue mints the WhatsApp unit server-side before it can navigate (WhatsApp alone and
    // "both" alike). Both bits exist because that call spends a round trip on a button whose next
    // screen spends money: the operator must not be able to press it twice, and a silent failure
    // must not look like a navigation that simply did not happen.
    const [creatingContainer, setCreatingContainer] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);
    // C-32 · the container request did not produce an id. Cleared on every fresh attempt.

    const waAllowed = useSelector((s: WaRootState) => selectWaAllowed(s));
    const waEnabled = useSelector((s: WaRootState) => selectWaEnabled(s));
    const availabilityStatus = useSelector((s: any) => s.smartSendWa.availabilityStatus);
    const withWhatsApp = useSelector((s: any) => s.smartSendWa.withWhatsApp);
    const waTemplateId = useSelector((s: any) => s.smartSendWa.whatsAppTemplateId);
    const waFromNumber = useSelector((s: any) => s.smartSendWa.whatsAppFromNumber);
    const waMapping = useSelector((s: any) => s.smartSendWa.waMapping);
    const waMappingCampaignId = useSelector((s: any) => s.smartSendWa.mappingCampaignId);
    const suppressEmail = useSelector((s: any) => s.smartSendWa.suppressEmail);
    const waPendingLegs = useSelector((s: any) => s.smartSendWa.waPendingLegs);
    // C-42 · the card the template picker already wrote — never a lookup back through
    // `waTemplates` by id (`smartSendWhatsAppSlice.ts:89`, written by `selectWaTemplate`).
    const waTemplateCard = useSelector((s: any) => s.smartSendWa.waTemplateCard);
    // ⚠️ INTENT, NOT STATE (C-29). The bar is a DISPLAY, so it has to follow the switch the
    // operator just moved rather than the leg that does not exist yet — `selectEmailOnIntent`
    // is `¬(waActive ∧ suppressEmail)`, which is exactly what the tabs and the source gate read.
    // Both are plain booleans: a selector returning a fresh OBJECT would re-render this screen
    // on every dispatch in the app (selectors.ts:136-138).
    const waActive = useSelector((s: WaRootState) => selectWaActive(s));
    const emailOnIntent = useSelector((s: WaRootState) => selectEmailOnIntent(s));

    // 09-channel-units — THIS SCREEN CARRIES NO UNITS; IT IS WHERE THEY ARE CHOSEN. The store outlives
    // an in-app navigation, and a mapping page leaves its units in it (`unitMode`, and the unit's own
    // mapping, which reports HasLeg = 1). Every intent selector answers `unitMode` first, so without
    // this the tile and the e-mail switch would do nothing and Continue would repeat the LAST page's
    // channels. Declared first so it runs before any other effect of the first commit. The
    // operator's own choices (the tick, the template, the number, the e-mail switch) are kept.
    useEffect(() => {
        dispatch(setWaUnit({ unitId: null, unitMode: null }));
    }, [dispatch]);

    // QA BUG-29 — AND THE E-MAIL SLICE STARTS FROM THIS SCREEN'S URL TOO. The mapping page leaves its
    // campaign's source, columns, token map and saved mapping in `smartSend`; read here they preselected
    // the previous campaign's source, raised a phantom «התבקש מקור אחר» (that page's URL source against
    // its row's source) and an "unsaved changes" dialog over that page's server mapping. `resetForPicker`
    // clears exactly that scope (not `clearSmartSend`). Re-run when the URL's campaign or source changes.
    // `scopeClean` holds the source grid (and Continue) back until the reset has run: a child's effects
    // run before its parent's, so a grid mounted on the first commit would preselect from the stale slice.
    // QA BUG-31 — and the WhatsApp MESSAGE intent too (`resetWaForPicker`: the tick, the template, the
    // number, the e-mail switch), so a new campaign is never pre-ticked with the last visit's template.
    // EXCEPT on the «החלף מקור» → «אפס והמשך» round trip, which lands on a bare `/SmartSend`: there the
    // operator is changing the source of the SAME send, and its channels stay. The trip is told by a
    // ONE-SHOT marker (r1), taken — read and removed — on every entry, so an abandoned trip can never keep
    // the intent for a later entry. (A campaign click inside this screen keeps the message choice —
    // `resetWaIntentForCampaign`.)
    // QA BUG-32 — and every OTHER entry drops all reset confirmations (the '1' key of an abandoned trip, a
    // target key that never reached its mapping page): a confirmation answers the step right after it,
    // never a later visit — otherwise R1 skipped its dialog and reset silently, and R2 did not redirect.
    const scopeParamsKey = `${campaignIdParam ?? ''}|${dataSourceId ?? ''}`;
    const [scopeKey, setScopeKey] = useState<string | null>(null);
    // FU-06A — the wizard's own state (see "THE WIZARD" below): the step the operator is on (null until the
    // first render pins it), and whether step ② «איך שולחים?» is answered. Reset with the scope. Step ② starts
    // answered where the channels are already known: on the change-source trip (they were kept) and when a
    // campaign came in the URL (R2 — «אימייל» by default, and the operator may choose otherwise).
    const [active, setActive] = useState<WizardStep | null>(null);
    const [chanChosen, setChanChosen] = useState(false);
    // r3 — one number per entry (URL scope). A Continue remembers the number it was pressed under; an answer that
    // lands under another one — even after Back and Forward to the same URL — does nothing, and touches no flag of
    // a newer request.
    const scopeGen = useRef(0);
    useEffect(() => {
        scopeGen.current += 1;
        dispatch(resetForPicker());
        const trip = takeChangeSourceTrip();
        const changeSourceTrip = trip && campaignIdParam == null && !dataSourceId;
        if (!changeSourceTrip) {
            clearAllConfirmResets();
            dispatch(resetWaForPicker());
        }
        setActive(null);
        setChanChosen(changeSourceTrip || seededCampaignId != null);
        // r2 — a new URL (Back/Forward on the same route) also drops what belonged to the previous one: an open R1
        // question, a Continue still in flight (its late answer is discarded by the entry number and URL check), a
        // container error.
        setSwitchAsk(null);
        setCheckingRow(false);
        setCreatingContainer(false);
        setCreateError(null);
        // QA BUG-35 — and the joined-unit question, a Leave in flight, a template still to be filled in.
        setUnitAsk(null);
        setLeavingUnit(false);
        keepPrefill.current = null;
        setScopeKey(scopeParamsKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, scopeParamsKey]);
    const scopeClean = scopeKey === scopeParamsKey;

    // QA BUG-29 · Idan R2 — «שליחה ממקור» from campaign management (`?campaignId=Y`, no source): a campaign
    // that already has a mapping opens its mapping page DIRECTLY, on its own source; an unmapped one stays
    // here to pick a source. One read of the campaign's row (`peekMapping`, which writes nothing).
    // QA BUG-32 — no exemption for an armed '1' key any more: this door carries `?campaignId`, so it is never
    // the change-source trip (that lands on a bare `/SmartSend`), and the entry effect above has already
    // dropped any key left behind.
    // A failed read, or an answer for parameters already left, opens the normal pick.
    const routerNavigate = useNavigate();
    const seedNeedsCheck = seededCampaignId != null && !dataSourceId;
    const [seedCheckedFor, setSeedCheckedFor] = useState<string | null>(null);
    const seedChecking = seedNeedsCheck && seedCheckedFor !== scopeParamsKey;
    const liveParamsKey = useRef(scopeParamsKey);
    liveParamsKey.current = scopeParamsKey;
    useEffect(() => {
        if (!seedNeedsCheck || seededCampaignId == null) return undefined;
        const key = scopeParamsKey;
        let alive = true;
        (async () => {
            const res: any = await dispatch(peekMapping({ campaignId: seededCampaignId, channel: eSendChannel.EMAIL }) as any);
            if (!alive || liveParamsKey.current !== key) return;
            const p: any = res?.payload;
            const rowDs = Number(p?.Data?.DataSource?.DataSourceID);
            if (p?.StatusCode === 200 && p?.Data?.IsMapped && Number.isInteger(rowDs) && rowDs > 0) {
                routerNavigate(`${sitePrefix}Campaigns/SmartSend/${seededCampaignId}?dataSourceId=${rowDs}`, { replace: true });
                return;
            }
            setSeedCheckedFor(key);
        })();
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, seedNeedsCheck, seededCampaignId, scopeParamsKey]);

    useEffect(() => {
        // `accountFeatures?.length &&` — the house precedent (DataSources.tsx:90,
        // DataSourceView.tsx:73), and all the feature gates must agree or the same user gets
        // bounced by one screen and admitted by another.
        // What the store actually does: `commonSlice.js:239` initialises accountFeatures to
        // `null`, and `:294` assigns `data?.Account?.AccountFeatures?.map(String)` on
        // getCommonFeatures.fulfilled — an array, or `undefined` when that payload carries no
        // AccountFeatures. There is NO `[]`-during-load state, so the load window is already
        // covered by plain falsiness; `?.length` is not what protects it.
        // What `?.length` adds is the empty array: an `[]` (or the `undefined` above) means the
        // features answer told us nothing, and we decline to evict the user on it — the route
        // guard and the API are the authoritative gates, and a wrong redirect here is
        // unrecoverable for the user while a wrong admission is not.
        if (accountFeatures?.length && accountFeatures.indexOf(PulseemFeatures.DATA_SOURCES) === -1) {
            // `sitePrefix` is `string | undefined` (it comes straight from process.env), so it
            // needs the same `?? ''` the other feature-gate redirects use — see
            // DataSources.tsx:91.
            Redirect({ url: sitePrefix ?? '', openNewTab: false });
        }
    }, [accountFeatures]);

    // `Availability` is ALWAYS the first WhatsApp call (contract §6) and it never 4xxs. Nothing
    // else is fetched until it answers Enabled — that is what keeps an unentitled account's
    // network tab exactly as it is today.
    useEffect(() => {
        if (waAllowed && availabilityStatus === 'idle') dispatch(getWaAvailability());
    }, [waAllowed, availabilityStatus, dispatch]);

    // 09-channel-units — no "pending legs" list and no leg read on a campaign pick: WhatsApp never
    // hangs on an e-mail campaign any more, so there is no leg to list, show or remove here.
    //
    // 🔴 C2 · 2026-09-20 — THE INTENT RESET ON A CAMPAIGN CHANGE STAYS. This screen never dispatches
    // `setCampaignContext`, so nothing else re-keys the slice here. `resetWaIntentForCampaign`
    // clears campaign-scoped state (the e-mail switch, the working map) and keeps the operator's
    // message choice (the tick, the template, the number); it is self-guarded on the id, so
    // re-clicking the SAME row keeps the operator's tick.
    //
    // 🔴 2026-09-23 — AND IT ONLY FIRES WHEN A DIFFERENT CAMPAIGN WAS ALREADY PICKED HERE: on the
    // first pick there is no previous campaign, and the tick and template are always configured
    // before the first campaign click (the WhatsApp tile is step 1, the campaign grid step 2).
    // The previous pick is answered from this screen's own state; `campaign`/`pendingLeg` are set
    // synchronously and have no in-flight window.
    const pickedIdNow = () => (pendingLeg ? pendingLeg.CampaignID : (campaign?.CampaignID ?? null));

    const onPickCampaign = (row: CampaignRow) => {
        const prevId = pickedIdNow();
        if (prevId != null && prevId !== row.CampaignID) dispatch(resetWaIntentForCampaign(row.CampaignID));
        setPendingLeg(null);
        setCampaign(row);
    };

    // 09-channel-units — never reached: the pending-legs list is empty because it is no longer
    // fetched. Kept only because CampaignPicker requires the callback.
    const onPickPending = (leg: WaPendingLeg) => {
        const prevId = pickedIdNow();
        if (prevId != null && prevId !== leg.CampaignID) dispatch(resetWaIntentForCampaign(leg.CampaignID));
        setCampaign(null);
        setPendingLeg(leg);
    };

    // Name of the source the user arrived from. Resolved from `dataSources.list` — which, FU-06A, this screen
    // now fetches itself when the URL's row is missing (see "THE URL'S SOURCE ROW" below). An id that is still
    // not in it resolves to nothing: a bare numeric id is meaningless to the user.
    const fromSourceName: string | null = useMemo(() => {
        const idNum = Number(dataSourceId);
        if (!dataSourceId || Number.isNaN(idNum) || idNum <= 0) return null;
        const items: any[] = dataSourceList?.items ?? [];
        return items.find((it: any) => it.DataSourceID === idNum)?.Name ?? null;
    }, [dataSourceId, dataSourceList]);

    // 12-fix-wave 7.3 (D4) — THE SELECTED SOURCE'S MOBILE COLUMN, from its GetMany row
    // (`DataSourceListItem.HasCellIdentity`, the flag SourcePicker gates the cards with). The source is
    // the URL's when the door brought one, else the one picked on this screen — the same order
    // `navigate` uses. Like `fromSourceName`, read from `dataSources.list` (fetched below when the URL's row is
    // missing); a source still unknown leaves the WhatsApp option enabled and the server's Continue refusal is the net.
    const pickerSourceRow: any = useMemo(() => {
        const idNum = dataSourceId ? Number(dataSourceId) : (pickedSourceId ?? NaN);
        if (Number.isNaN(idNum) || idNum <= 0) return null;
        const items: any[] = dataSourceList?.items ?? [];
        return items.find((it: any) => it.DataSourceID === idNum) ?? null;
    }, [dataSourceId, pickedSourceId, dataSourceList]);
    const sourceNoCell = !!pickerSourceRow && pickerSourceRow.HasCellIdentity === false;

    // FU-06A — THE URL'S SOURCE ROW IS NOW FETCHED WHEN IT IS MISSING. The wizard reads it for step ② (which
    // channels its columns serve) and for step ⑤ (its name and recipients); a cold entry or a refresh of
    // `?dataSourceId=` left the list unloaded and the review said «מקור: —». The same request SourcePicker makes
    // (one page of 100, which also replaces whatever page the DataSources screen left in the shared list), once per
    // URL source and only when the row is not there. The wizard waits for it like it waits for Availability; an id
    // still not found after it (101+ sources, a deleted source) leaves the row unknown, as before.
    const dataSourcesListStatus = useSelector((s: any) => s.dataSources.listStatus);
    const urlRowFetchedFor = useRef<string | null>(null);
    const needUrlRow = !!dataSourceId && !pickerSourceRow;
    const urlRowSettled = !needUrlRow
        || (urlRowFetchedFor.current === dataSourceId && dataSourcesListStatus !== 'idle' && dataSourcesListStatus !== 'loading');
    useEffect(() => {
        if (!scopeClean || !needUrlRow || urlRowFetchedFor.current === dataSourceId) return;
        urlRowFetchedFor.current = dataSourceId;
        dispatch(getDataSources({ PageIndex: 1, PageSize: 100, SearchTerm: '' }));
    }, [scopeClean, needUrlRow, dataSourceId, dispatch]);

    // 🔴 2026-09-23 — `?? seededCampaignId` is the campaign-side half of "one road in".
    // A door that already knows the campaign (campaign management, the BEE editor) hands it over
    // in the URL, and the grid renders it selected without the operator re-picking it. A manual
    // pick overrides it the moment it happens, because `campaign` is checked first.
    // NOTE it is not routed through `onPickCampaign`: there is no previous pick to reset (and,
    // since 09-channel-units, no leg is read on a pick at all).
    // ⚠️ THE SEED COUNTS ONLY FOR A CAMPAIGN THE GRID WOULD LET THE OPERATOR PICK. A URL can name a
    // campaign that is already sent, built in the old editor, or sent by an automation; the grid
    // shows such a row disabled, and Continue must not take it through the back door. Until the
    // list (CampaignPicker's own fetch) has the row, the seed is unconfirmed and Continue waits.
    // Both lists of that one response: the campaigns-management rows offer this door on child rows
    // too (NewsletterManagment.js renders the same actions for them).
    const parentRows: CampaignRow[] = useSelector((s: any) => s.newsletter.newslettersParentCampaigns);
    const childRows: CampaignRow[] = useSelector((s: any) => s.newsletter.newslettersChildCampaigns);
    const seededRow: CampaignRow | null = useMemo(() => {
        if (seededCampaignId == null) return null;
        return (parentRows ?? []).find((r) => r.CampaignID === seededCampaignId)
            ?? (childRows ?? []).find((r) => r.CampaignID === seededCampaignId)
            ?? null;
    }, [seededCampaignId, parentRows, childRows]);
    const seededPickable = seededRow && isSelectable(seededRow) ? seededCampaignId : null;
    // A campaign handed over in the URL that cannot be sent from a source is SAID, not silently
    // dropped: the grid hides unsendable rows by default, so without this the operator would find
    // their campaign neither selected nor listed. Only until they pick another one.
    // FU-06A r1 — the name isolated (FSI…PDI) inside the RTL sentence, so a Latin/numeric name is not reordered.
    const seedRefusedText: string | null = seededRow && !isSelectable(seededRow) && campaign == null
        ? t('DataSources.send.picker.seedRefused', { name: isolateBidi(seededRow.Name ?? ''), reason: t(blockedReasonKey(seededRow)) })
        : null;
    const selectedId = pendingLeg ? pendingLeg.CampaignID : (campaign?.CampaignID ?? seededPickable);
    // The leg the server reported for the CURRENTLY selected campaign — never a leg left over
    // from a previous selection (the same campaignId equality guard SmartSendScreen applies to
    // `savedMapping` and `campaignName`, and for the same reason).
    // 🔴 `mappingCampaignId === selectedId` IS THE GUARD, not a tidiness check: a mapping in the
    // slice is only ever the SELECTED campaign's.
    // 09-channel-units — this screen no longer reads a leg at all, so this is null unless a slice
    // left over from another screen happens to hold this very campaign's leg; every consumer below
    // (the tile, the template picker, the chip) already treats null as "no leg".
    const legForSelection = (waMapping && selectedId != null
        && waMappingCampaignId === selectedId && waMapping.HasLeg) ? waMapping : null;
    const emailAlreadySent = !!legForSelection
        && EMAIL_SENT_STATUSES.indexOf(legForSelection.EmailCampaignStatus ?? -1) > -1;

    // ── FU-06A · THE CHANNELS ARE MOVED BY THE TWO CONTROLS THAT ALWAYS MOVED THEM, AND ONLY AS THEY COULD ──
    // The channel row (ChannelSelector's e-mail switch + WaAddonCard's tile) is gone from this screen; its two
    // writes are not. A card of step ② «איך שולחים?» is reached by the shortest sequence of the clicks that row
    // would have accepted — `channelPath` in pickerWizard.ts holds each click to that control's own guard:
    //   · the tile: `setWithWhatsApp(next)`, only while WaAddonCard's `interactive` holds (Ready, the mail of the
    //     selected campaign not sent, not the last lit channel, not a no-cell source being turned on);
    //   · the e-mail switch: what `emailToggle.onToggle(next)` dispatched, `setSuppressEmail(!next)`, only while
    //     the tile is ticked and the mail of the selected campaign has not gone out.
    // "Both channels off" stays unreachable by the same two gates as before: the tile refuses to un-tick the last
    // lit channel, and `setWithWhatsApp(false)` restores the e-mail in the reducer.
    // `legForSelection` (never the raw `waMapping`) is the leg those guards read, as it was.
    const runChannelActions = (actions: ChannelAction[]) => {
        actions.forEach((a) => {
            if (a.kind === 'tile') dispatch(setWithWhatsApp(a.next));
            else dispatch(setSuppressEmail(!a.next));
        });
    };

    // 🔴 THE CHOICE TRAVELS IN THE URL, AND IT HAS TO — the slice cannot carry it.
    // `SmartSendScreen` dispatches `setCampaignContext` on mount, and this screen never does, so
    // the ids differ and the reducer wipes the ENTIRE WhatsApp slice before anything else runs.
    // That is why `?waTemplate=` and `?waFrom=` exist at all: they paint the template card on the
    // first frame, before the unit's own `GET WhatsApp/Mapping` has answered.
    // 09-channel-units — `wa` names the WhatsApp UNIT sent together with the e-mail campaign in the
    // path ("both"); `channel=3` says the id in the path IS the WhatsApp unit ("WhatsApp only").
    // 12-fix-wave 7.3 — no `wa` parameter any more: the mapping page reads the send's WhatsApp from the
    // DB (D5). "Both" and e-mail only open `/SmartSend/{c}`; WhatsApp only opens `/SmartSend/{w}?channel=3`.
    const navigate = (id: number, toWhatsApp: boolean, channel = 1, reused = false) => {
        // encodeURIComponent rather than raw interpolation: `dataSourceId` is passed straight
        // through from the address bar, so a stray '&' or '#' would otherwise rewrite the query.
        const qs = new URLSearchParams();
        // 🔴 2026-09-23 — the URL's source if it brought one, otherwise the one chosen on this
        // screen, so the mapping screen never has to ask for the source again.
        const dsForUrl = dataSourceId || (pickedSourceId != null ? String(pickedSourceId) : '');
        if (dsForUrl) qs.set('dataSourceId', dsForUrl);
        if (toWhatsApp) {
            qs.set('tab', 'whatsapp');
            // The ContentSid, never the numeric Id (v1.5). The unit was created FROM it a moment
            // ago, so it is the unit's own template, not a guess.
            if (waTemplateId) qs.set('waTemplate', waTemplateId);
            if (waFromNumber) qs.set('waFrom', waFromNumber);
            // 🔴 Which TABLE the id in the path belongs to. Without it the mapping screen asks
            // GET Mapping for a CampaignID that lives in dbo.WhatsAppCampaign and the ownership
            // gate answers 404 — correctly, and a dead end for the operator.
            if (channel === 3) qs.set('channel', '3');
            // The server handed back the existing unit for this choice (FindReusableWaUnit) rather
            // than a new one; the WhatsApp tab says so, because its map is already filled.
            if (reused) qs.set('reused', '1');
        }
        const q = qs.toString();
        // QA BUG-32 — the one-shot mark that lets THIS e-mail page honour a reset confirmation armed here
        // (or on the change-source trip); a later visit to that page finds no mark and asks again.
        if (channel !== 3) markConfirmEntry(id);
        Redirect({ url: `${sitePrefix}Campaigns/SmartSend/${id}${q ? `?${q}` : ''}`, openNewTab: false });
    };

    // 09-channel-units — creates the WhatsApp UNIT: the container from the chosen template AND the
    // unit's own mapping row from the source, in ONE request (`POST WhatsApp/Container`). Returns the
    // unit's WACampaignID, or null with the server's refusal code. The server writes the row through
    // the same writer the e-mail uses, which proves the source is this sub-account's, Ready and
    // carries a cell column, and removes the container again if the row is refused — so a failed
    // Continue leaves no half-made unit behind.
    // 2026-09-28 — THE SAME CHOICE IS THE SAME UNIT: when an unsent unit with this source, template and
    // sender number exists, the server returns IT (`Reused`), map included, instead of a new container.
    const createWaUnit = async (): Promise<{ id: number | null; message: string | null; reused: boolean }> => {
        const dsNum = Number(dataSourceId || pickedSourceId);
        // No source, no unit: a container without its own row is not a unit the mapping screen can
        // load (its GET answers -16), and the server no longer binds one after the fact.
        // `continueBlocked` already demands a source; this states it where the request is built.
        if (!Number.isInteger(dsNum) || dsNum <= 0) return { id: null, message: null, reused: false };
        const res: any = await dispatch(createWaContainer({
            whatsAppTemplateID: waTemplateId,
            fromNumber: waFromNumber,
            name: null,
            dataSourceId: dsNum,
            // 12-fix-wave 7.3 — "both": the selected e-mail campaign, so the server joins the unit to it.
            campaignId: channels.email ? selectedId : null,
        }) as any);
        // PulseemResponse wraps the payload: { StatusCode, Message, Data }. Reading
        // res.payload.WACampaignID straight off the envelope is always undefined.
        const p: any = res?.payload;
        const okStatus = !!p && (p.StatusCode === 200 || p.StatusCode === 201);
        const id = Number(p?.Data?.WACampaignID ?? p?.Data?.wACampaignID);
        if (!res || res.error || !okStatus || !id || Number.isNaN(id) || id <= 0) {
            return { id: null, message: typeof p?.Message === 'string' ? p.Message : null, reused: false };
        }
        return { id, message: null, reused: p?.Data?.Reused === true };
    };

    // QA BUG-29 · Idan R1 — «ברגע שאני לוחץ על האייקון של בחירת קמפיין לשליחה ממקור זה - זה חייב להיות
    // המקור הזה ואסור שישתנה בשום צורה». The source this screen continues with (the URL's, else the one
    // picked here) is FIXED. When the e-mail campaign's send is already mapped to ANOTHER source, the
    // operator is told that its previous mapping will be reset, BEFORE leaving: «המשך» carries the decision
    // to the mapping page (the confirmation key holds the target source), which resets on entry with ONE
    // save of that source + ConfirmReset (the server resets both channels, Set :295-333); «ביטול» stays.
    // Not asked when this campaign's key already holds this target, and not asked twice when «החלף מקור»
    // → «אפס והמשך» already confirmed (the '1' key): the key is re-armed with the target instead.
    // A closed send is not decided here: a sent e-mail campaign is not selectable in the grid, and a send
    // closed by its WhatsApp is refused by the mapping page (no switch, the row's source stays).
    // A failed read asks nothing here; the mapping page's entry check is the net.
    const [checkingRow, setCheckingRow] = useState(false);
    // QA BUG-35 — the WhatsApp unit already joined to the e-mail campaign's send, asked about when ② is «אימייל» (I8).
    // `canKeep`: «השאר אימייל + וואטסאפ» is offered (② can become E+W through the guards). `info`: the unit's template and
    // n/m, from a read that writes nothing (null when it failed, or when the account's WhatsApp is off — no WhatsApp/*
    // call is made then).
    type UnitAsk = { campaignId: number; waCampaignId: number; target: number; info: WaUnitSummary | null };
    const [unitAsk, setUnitAsk] = useState<UnitAsk | null>(null);
    const shownUnitAsk = useRef(unitAsk);
    if (unitAsk) shownUnitAsk.current = unitAsk;
    // The question can open in the very click that closed the source question (R1 «המשך»): an answer within
    // CLICK_GAP_MS of its opening is a carried click and is dropped (`acceptsClick`), the dialog stays.
    const unitAskShownAt = useRef(0);
    const openUnitAsk = (u: UnitAsk) => { unitAskShownAt.current = Date.now(); setUnitAsk(u); };
    // POST Leave of «הסר וואטסאפ» in flight.
    const [leavingUnit, setLeavingUnit] = useState(false);
    // «השאר»: the unit's own template and number, filled into ④ once the template list is in (see the effect below).
    const keepPrefill = useRef<{ templateId: string | null; fromNumber: string | null } | null>(null);
    const [switchAsk, setSwitchAsk] = useState<null | { campaignId: number; target: number; from: string; to: string; unit: UnitAsk | null }>(null);
    // What the dialog SHOWS: the last question asked, so its names do not blank while it fades out.
    const shownAsk = useRef(switchAsk);
    if (switchAsk) shownAsk.current = switchAsk;
    // QA BUG-32 — a Continue whose row read lands after the operator left this screen does nothing.
    const mountedRef = useRef(true);
    useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

    const goToMapping = async () => {
        if (creatingContainer || checkingRow || leavingUnit) return;
        // FU-06A r2/r3 — the entry this Continue was pressed under (`scopeGen`). A same-route navigation (browser
        // Back/Forward between /SmartSend URLs, even A→B→A) keeps this screen mounted but starts a new entry; an answer
        // that lands under another entry carries the old choices nowhere and touches no flag (the entry effect already
        // cleared them, and a newer request may own them now).
        const pressedOn = scopeGen.current;
        // … and the URL itself (set during render, so it also covers the moment between the new URL's render and
        // its entry effect, before the number moves).
        const pressedKey = scopeParamsKey;
        const stale = () => !mountedRef.current || scopeGen.current !== pressedOn || liveParamsKey.current !== pressedKey;
        if (channels.email && selectedId != null) {
            const target = Number(dataSourceId || pickedSourceId);
            const sourceCheck = Number.isInteger(target) && target > 0 && confirmResetTarget(eSendChannel.EMAIL, selectedId) !== target;
            // QA BUG-35 · I8 — «אימייל» alone ALWAYS reads the send's row (the same read as the source check, one request):
            // a WhatsApp unit an earlier «אימייל + וואטסאפ» joined to it would go out with this e-mail (`continueDecision`).
            const unitCheck = !channels.whatsapp;
            if (sourceCheck || unitCheck) {
                setCreateError(null);
                setCheckingRow(true);
                let row: any = null;
                try {
                    const res: any = await dispatch(peekMapping({ campaignId: selectedId, channel: eSendChannel.EMAIL }) as any);
                    row = res?.payload?.StatusCode === 200 ? res.payload.Data : null;
                } catch {
                    row = null;
                }
                if (stale()) return;
                const rowWa = Number(row?.RowWACampaignID);
                const hasUnit = Number.isInteger(rowWa) && rowWa > 0;
                const decision = continueDecision(channels, { read: row != null, wa: hasUnit });
                // The unit's template and n/m, for the question only — a second read that writes nothing, and only where
                // the account's WhatsApp is on (no WhatsApp/* call otherwise; the question then names no template).
                let info: WaUnitSummary | null = null;
                if (decision === 'askUnit' && guard.waAccount) {
                    try {
                        const r2: any = await dispatch(peekWaMapping(rowWa) as any);
                        info = r2?.payload?.StatusCode === 200 ? waUnitSummary(r2.payload.Data) : null;
                    } catch {
                        info = null;
                    }
                    if (stale()) return;
                }
                setCheckingRow(false);
                if (decision === 'readFailed') {
                    setCreateError(W('unitAsk.readFailed'));
                    return;
                }
                const unit: UnitAsk | null = decision === 'askUnit'
                    ? { campaignId: selectedId, waCampaignId: rowWa, target, info }
                    : null;
                const rowDs = Number(row?.DataSource?.DataSourceID);
                if (sourceCheck && row?.IsMapped && Number.isInteger(rowDs) && rowDs > 0 && rowDs !== target) {
                    if (isConfirmResetArmed(eSendChannel.EMAIL, selectedId)) {
                        armConfirmReset(eSendChannel.EMAIL, selectedId, target);
                    } else {
                        // The source question first; its «המשך» asks the unit question next (`unit`).
                        setSwitchAsk({
                            campaignId: selectedId,
                            target,
                            from: row?.DataSource?.Name ?? String(rowDs),
                            to: pickerSourceRow?.Name ?? String(target),
                            unit,
                        });
                        return;
                    }
                }
                if (unit) {
                    openUnitAsk(unit);
                    return;
                }
            }
        }
        await proceedToMapping();
    };

    // The screen must still be the one a question was asked about: the same campaign and source, «אימייל» alone, and
    // today's gate still open. Otherwise an answer does nothing.
    const askStillHolds = (ask: { campaignId: number; target: number }) => wv.finalEnabled && channels.email && !channels.whatsapp
        && ask.campaignId === selectedId && ask.target === Number(dataSourceId || pickedSourceId);

    // «הסר וואטסאפ והמשך באימייל בלבד» — the mapping page's own «הסר» (POST Leave: the row is split, nothing is deleted,
    // the unit keeps a row of its own), then the e-mail-only page. Only on 200 (`leaveOutcome`); otherwise stay and say why.
    const onUnitRemove = async () => {
        if (!acceptsClick(unitAskShownAt.current, Date.now())) return;
        const ask = unitAsk;
        setUnitAsk(null);
        if (!ask || leavingUnit || creatingContainer || checkingRow || !askStillHolds(ask)) return;
        const pressedOn = scopeGen.current;
        const pressedKey = scopeParamsKey;
        const stale = () => !mountedRef.current || scopeGen.current !== pressedOn || liveParamsKey.current !== pressedKey;
        setCreateError(null);
        setLeavingUnit(true);
        let p: any = null;
        try {
            const res: any = await dispatch(leaveSend({ campaignId: ask.campaignId, waCampaignId: ask.waCampaignId }) as any);
            p = res?.payload ?? null;
        } catch {
            p = null;
        }
        if (stale()) return;
        setLeavingUnit(false);
        const outcome = leaveOutcome(p?.StatusCode, p?.Message);
        if (outcome === 'ok') {
            await proceedToMapping();
            return;
        }
        if (outcome === 'sent') setCreateError(t('DataSources.send.errors.sendClosed'));
        else if (outcome === 'changed') setCreateError(W('unitAsk.changed'));
        else setCreateError(t('DataSources.send.errors.generalError'));
    };

    // «השאר אימייל + וואטסאפ» — ② becomes E+W through the guards (a click on that card: `keepBothPath`), and the operator
    // continues from ④ with the unit's own template and number filled in, so the same choice returns the same unit
    // (FindReusableWaUnit) and «המשך למיפוי השדות» joins it again. Nothing is sent or written here.
    const onUnitKeep = () => {
        if (!acceptsClick(unitAskShownAt.current, Date.now())) return;
        const ask = unitAsk;
        setUnitAsk(null);
        if (!ask || busy || !askStillHolds(ask)) return;
        const path = keepBothPath(wizardIn);
        if (path == null) return;
        runChannelActions(path);
        setChanChosen(true);
        keepPrefill.current = ask.info ? { templateId: ask.info.templateId, fromNumber: ask.info.fromNumber } : null;
        stepShownAt.current = Date.now();
        setActive('whatsapp');
    };

    const proceedToMapping = async () => {
        if (creatingContainer) return;
        // FU-06A r2/r3 — see goToMapping: the entry number and the URL this Continue was pressed under.
        const pressedOn = scopeGen.current;
        const pressedKey = scopeParamsKey;
        const stale = () => !mountedRef.current || scopeGen.current !== pressedOn || liveParamsKey.current !== pressedKey;

        // 🔴 EVERY LIVE CHANNEL LEAVES WITH ITS OWN UNIT, and the WhatsApp one is created HERE —
        // alone or together with the e-mail, by the same request. Nothing on the mapping screen
        // creates a unit, so nothing there can wait on one.
        if (channels.whatsapp) {
            if (!waTemplateId) return;
            // "both" also needs the e-mail's container; WhatsApp alone needs none.
            if (channels.email && selectedId == null) return;
            setCreateError(null);
            setCreatingContainer(true);
            try {
                const unit = await createWaUnit();
                // The screen started another entry while the unit was being created: nothing navigates, and no flag of
                // the new entry is touched. (A unit created for the old choice stays unsent; the same choice later
                // returns it — FindReusableWaUnit.)
                if (stale()) return;
                if (unit.id == null) {
                    setCreatingContainer(false);
                    // The server's own reason when it names one the operator can act on — a source
                    // with no mobile column, a template with media — instead of a "try again" that
                    // can never succeed. The generic sentence otherwise.
                    const waKey = unit.message ? `DataSources.send.whatsapp.error.${unit.message}` : null;
                    setCreateError(waKey && i18n.exists(waKey) ? t(waKey) : t('DataSources.send.picker.waContainerFailed'));
                    return;
                }
                if (channels.email) navigate(selectedId as number, true, 1, unit.reused);
                else navigate(unit.id, true, 3, unit.reused);
            } catch {
                if (stale()) return;
                setCreatingContainer(false);
                setCreateError(t('DataSources.send.picker.waContainerFailed'));
            }
            return;
        }

        if (selectedId == null) return;
        navigate(selectedId, false);
    };

    // ── C-32 / C-43 · THE ACTIVE SET, AND EVERYTHING DERIVED FROM IT ────────────────────────
    // One set, read by everything that asks "who is on": which steps of the wizard exist, and whether
    // Continue is live (FU-06A: the numbered content headings and the bottom chips are the wizard's now).
    // ⚠️ MEMOISED ON THE TWO SCALARS: `activeKeys` is an array, and a fresh one each render is a fresh
    // dependency each render.
    const channels = useMemo(
        () => channelsFrom({ email: emailOnIntent, whatsapp: waActive }),
        [emailOnIntent, waActive],
    );
    const activeKeys = useMemo(() => activeChannelKeys(channels), [channels]);

    // 🔴 C-32 · "«המשך» נדלק כשלכל ערוץ דלוק יש תוכן. ערוץ כבוי אינו דורש תוכן." (48 §2.5)
    // The old first clause was an unconditional `selectedId == null`, which is the E-MAIL's
    // content requirement stated as if it were everyone's: with the mail off there is no
    // campaign to pick and the server makes the container, so demanding one there is demanding
    // the thing this mode exists to avoid.
    // ⚠️ AND THE FIRST ARM IS NOT DEAD CODE. "Zero channels" is refused by the tile (C-43ב) and
    // by the reducer, but a gate that assumes another gate held is the shape this package has
    // been bitten by four times; it costs one comparison to answer for itself.
    // 🔴 70 (4.4) — `selectedId == null` IS UNCONDITIONAL NOW, not `channels.email && ...`.
    // The e-mail-off arm existed because the server was going to mint a container; that is
    // cancelled (66 (3.1)), and the campaign is the container of every send on every channel.
    // ⚠️ The first arm is still not dead code. "Zero channels" is refused by the tile (C-43ב)
    // and by the reducer, but a gate that assumes another gate held is the shape this package
    // has been bitten by four times; it costs one comparison to answer for itself.
    // 🔴 77 §1 — ONE CLAUSE PER LIVE CHANNEL, AND EACH ANSWERS ONLY FOR ITSELF.
    // What stood here demanded `selectedId != null` UNCONDITIONALLY, i.e. an e-mail campaign on
    // every channel mix — the 70 (4.4) rule that 77 §3 cancels. With the e-mail off that clause
    // could never be satisfied (the grid does not render, so nothing can set `campaign`), which
    // is why "WhatsApp alone" was unreachable from this screen no matter what else was fixed.
    // Now: the e-mail asks for its container only while the e-mail is lit, and the WhatsApp asks
    // for its own only when it is standing alone. Neither reads the other's field.
    const emailNeedsPick = channels.email && selectedId == null;
    // WhatsApp-only asks for a TEMPLATE, not a container: the container is derived from it.
    // (Owner, 21.09 — «בוטס אפ תבנית שיוצרים ממנה קמפיין».) With the e-mail on, the existing
    // leg rule already covers it, which is why this clause is the e-mail-off one only.
    // ⚠️ CORRECTED 2026-09-22. This used to read "SHUT UNTIL THE CONTAINER SEAM EXISTS …
    // Left closed deliberately", from the days when nothing turned a template into a
    // WACampaignID. The seam exists and is two screens down from this line:
    //   :301  dispatch(createWaContainer(...))  → POST api/DataSourcesSender/WhatsApp/Container
    //   :310  reads p?.Data?.WACampaignID       (the envelope, not the bare payload)
    //   :325  navigate(id, true, true, true, 3) → :270 writes ?channel=3
    // So the gate is no longer "closed": it is exactly `!waTemplateId`. Pick an approved
    // template and Continue opens, mints the container and navigates to its own mapping screen.
    const waNeedsPick = channels.whatsapp && !channels.email && !waTemplateId;
    // 🔴 2026-09-23 — and a source is now part of "ready", for the doors that arrive without one.
    // A door that DID bring `?dataSourceId=` is unaffected: `dataSourceId` is truthy and this
    // clause is false regardless of the slice, so those three doors behave exactly as before.
    const sourceNeedsPick = !dataSourceId && pickedSourceId == null;
    // FU-06 — the gate itself now lives in pickerSections.ts (`continueBlockedOf`), clause for clause the
    // expression that stood here, so the sections layer and its exhaustive test read the same definition:
    //   activeKeys.length === 0
    //   || !scopeClean                                   (QA BUG-29 — the slice reset for THIS URL)
    //   || sourceNeedsPick || emailNeedsPick || waNeedsPick
    //   || (channels.whatsapp && channels.email && !waTemplateId)   (09-channel-units — "both" needs a template)
    //   || (channels.whatsapp && sourceNoCell)          (12-fix-wave 7.3 D4 — no WhatsApp on a no-cell source;
    //                                                    the server cannot be the only net: a reused unit is not rebound)
    const continueGate: ContinueGateInput = {
        activeCount: activeKeys.length,
        scopeClean,
        sourceNeedsPick,
        emailNeedsPick,
        waNeedsPick,
        email: channels.email,
        whatsapp: channels.whatsapp,
        hasTemplate: !!waTemplateId,
        sourceNoCell,
    };
    // FU-06A — `continueBlockedOf(continueGate)` is read by the wizard (`finalEnabled`), unchanged.

    const isRTL = useSelector((s: any) => s.core && s.core.isRTL);

    // ══ FU-06A (Idan) — THE WIZARD: ONE QUESTION AT A TIME ═══════════════════════════════════════════════
    // ① מאיזה מקור שולחים? ② איך שולחים? ③ לאיזה קמפיין אימייל? ④ איזו תבנית וואטסאפ? ⑤ בדיקה לפני המיפוי.
    // EVERY decision — which steps this send has, which is ✓, which card is dimmed and why, where «המשך» and
    // «חזרה» go, whether «המשך למיפוי השדות» is live — is made in pickerWizard.ts (pure; pickerWizard.test.js
    // runs it, and the old screen beside it, over the whole state space). Nothing is decided here.
    // Mounting is untouched: each grid keeps the exact condition it had (the source grid `!dataSourceId &&
    // scopeClean`, the campaign grid `channels.email`, the template grid `channels.whatsapp`), and a step that is
    // not the active one is `display:none`, never unmounted. R2 (the seed check above, its spinner and its
    // redirect) is untouched and returns BEFORE any of this is drawn.
    // Each grid's own ✓ comes from pickerSections.ts (FU-06), the source's with the wizard's gate.
    const selectedSourceRow = useSelector((s: WaRootState) => selectSelectedSourceRow(s));
    const waOnlyIntent = useSelector((s: WaRootState) => selectWaOnlyIntent(s));
    const selectedChannel = useSelector((s: any) => s.smartSend.selectedChannel);
    const legNoCell = useSelector((s: any) => s.smartSendWa?.waMapping?.HasCellIdentity === false);
    const waTemplates = useSelector((s: any) => s.smartSendWa.waTemplates);
    const waTemplatesStatus = useSelector((s: any) => s.smartSendWa.templatesStatus);
    const waNumbers = useSelector((s: any) => s.smartSendWa.waAvailability?.FromNumbers);
    const waReady = useSelector((s: any) => !!s.smartSendWa.waAvailability?.Ready);
    const waBlockReason: string | null = useSelector((s: any) => s.smartSendWa.waAvailability?.BlockReason ?? null);
    const guard: GuardFacts = {
        waAccount: waAllowed && waEnabled,
        waReady,
        emailAlreadySent,
        noCell: sourceNoCell,
        hasLeg: !!waMapping?.HasLeg,
    };
    // QA BUG-35 — after «השאר אימייל + וואטסאפ»: ④ gets the joined unit's own template and number once the template list
    // is in (④ mounts only now, and loads it). A template no longer in the list, or blocked, and a number no longer
    // connected, are left for the operator to choose. Once.
    useEffect(() => {
        const want = keepPrefill.current;
        if (!want || !channels.whatsapp || waTemplatesStatus !== 'succeeded') return;
        keepPrefill.current = null;
        const tpl = want.templateId ? (waTemplates ?? []).find((x: any) => x.TemplateId === want.templateId) : null;
        if (tpl && templateBlockReason(tpl) == null) dispatch(selectWaTemplate(tpl));
        if (want.fromNumber && (waNumbers ?? []).some((n: any) => n.FromNumber === want.fromNumber)) {
            dispatch(setWaFromNumber(want.fromNumber));
        }
    }, [channels.whatsapp, waTemplatesStatus, waTemplates, waNumbers, dispatch]);
    // Step ①'s grid is gated on what the account can send (the channels are chosen in step ②).
    const gateChannels = sourceGateChannels(guard);
    const gateAll = channelsFrom(gateChannels);
    const sectionsInput: SectionsInput = sectionsInputFrom({
        channels,
        waActive,
        waOnly: waOnlyIntent,
        countField: getChannelDescriptor(selectedChannel).resolvedCountField,
        sourceMounted: !dataSourceId && scopeClean,
        sourceRow: selectedSourceRow,
        legNoCell,
        selectedId,
        pendingLegName: pendingLeg ? (pendingLeg.CampaignName || null) : null,
        campaignRows: parentRows ?? [],
        seedRefused: seedRefusedText != null,
        templateId: waTemplateId ?? null,
        templates: waTemplates ?? [],
        templatesStatus: waTemplatesStatus,
        numbers: waNumbers ?? [],
        fromNumber: waFromNumber ?? null,
        templateCardName: waTemplateCard?.name || null,
        openFor: {},
        gate: continueGate,
    }, {
        sourceCanSend: (row: any) => sourceCanSend(row, gateAll),
        missingIdentity,
        isCampaignSelectable: isSelectable,
        templateBlocked: (tpl: any) => templateBlockReason(tpl) != null,
    });
    const sv = sectionsView(sectionsInput);

    const waSettled = !waAllowed || availabilityStatus === 'succeeded' || availabilityStatus === 'failed';
    const wizardIn: WizardInput = {
        fromUrl: !!dataSourceId,
        intent: { withWhatsApp: !!withWhatsApp, suppressEmail: !!suppressEmail },
        guard,
        src: { known: !!pickerSourceRow, hasEmail: !!pickerSourceRow?.HasEmailIdentity },
        channels: { email: channels.email, whatsapp: channels.whatsapp },
        chanChosen,
        valid: {
            // SourcePicker's ✓: the picked source's row, READY, sendable on the account's channels. (Not its name:
            // a nameless row is still a source to send from.)
            source: !dataSourceId && scopeClean && !!selectedSourceRow
                && selectedSourceRow.Status === eDataSourceStatus.READY && sourceCanSend(selectedSourceRow, gateAll),
            // Exactly what today's gate asks of the e-mail: a campaign to send (a grid pick, or a seed the grid
            // would let the operator pick — parent or child row).
            email: selectedId != null,
            // The template grid's ✓: in the loaded list, usable, named, and a connected sender number chosen
            // whenever there are several.
            whatsapp: sv.sections.whatsapp.selectionKey != null,
        },
        active,
        gate: continueGate,
        // r2 — until WhatsApp answers, the bar does not say «לא נדרש» about ②/③/④ (it does not know yet).
        waUnknown: !waSettled,
    };
    const wv = wizardView(wizardIn);
    // Ready to draw: the slice reset for this URL. An entry that starts at step ① draws at once (the operator picks a
    // source while WhatsApp's own answer comes in, and «המשך» of step ① waits for it); the URL-source entry starts
    // at the first step still open (② … ⑤), which depends on that answer and on the source's row, so it waits for both. Step ② and the auto
    // choices are never settled before WhatsApp answered (`settled`).
    const settled = scopeClean && !seedChecking && waSettled && (!dataSourceId || urlRowSettled);
    // FU-06A r2 — the URL-source entry draws its first frame only once step ② has settled (the store moved to the
    // only possible card), so the step it starts on is already mounted. A fix that was dispatched and did not move
    // the store (`lastFix`) does not hold the screen.
    const lastFix = useRef<string | null>(null);
    const fixKeyOf = (fix: { resetWa: boolean; apply: ChannelAction[] | null }): string | null => (
        fix.resetWa || (fix.apply && fix.apply.length)
            ? `${!!withWhatsApp}|${!!suppressEmail}|${fix.resetWa ? 'resetWa' : JSON.stringify(fix.apply)}`
            : null);
    const initialFixKey = dataSourceId && active == null && settled ? fixKeyOf(wizardReconcile(wizardIn)) : null;
    const initialFixPending = initialFixKey != null && lastFix.current !== initialFixKey;
    const wizardLive = scopeClean && !seedChecking && (!dataSourceId || (settled && !initialFixPending));

    // The very keys SourcePicker and the old channel row used for the same facts.
    const REVIEW_WARNING_KEY: Record<Exclude<ReviewWarning, 'seedRefused'>, string> = {
        noEmail: 'DataSources.send.whatsapp.mapping.noEmailBanner',
        noEmailBoth: 'DataSources.send.whatsapp.mapping.noEmailBannerBoth',
        noCell: 'DataSources.send.whatsapp.mapping.noCellBanner',
        noCellBoth: 'DataSources.send.whatsapp.mapping.noCellBannerBoth',
        zeroEmail: 'DataSources.send.source.zeroResolved',
        zeroWa: 'DataSources.send.whatsapp.error.WA_NO_RECIPIENTS',
    };
    const countField = getChannelDescriptor(selectedChannel).resolvedCountField;
    const emailCount: number | null = pickerSourceRow ? Number(pickerSourceRow[countField] ?? 0) : null;
    const cellCount: number | null = pickerSourceRow ? Number(pickerSourceRow.ResolvedRowsCell ?? 0) : null;
    const warnings = reviewWarnings({
        channels: { email: channels.email, whatsapp: channels.whatsapp },
        waActive,
        srcMissing: pickerSourceRow ? missingIdentity(pickerSourceRow, channels) : 'none',
        legNoCell,
        emailCount,
        cellCount,
        seedRefused: seedRefusedText != null,
    });

    // After each render: step ② keeps up with the source (`wizardReconcile`: the only possible card is chosen
    // through the guards; a chosen card a new source dims re-opens the question). Never before the slice was reset
    // for this URL and WhatsApp answered — the render before that reads the previous visit's intent. A fix is
    // applied once per intent it was computed from (`lastFix`): if the store did not move, it is not dispatched again.
    // (Leaving ① is settled in the click itself — `moveTo`; this effect covers the URL-source entry, the account-level
    // reset and a chosen card re-opened.)
    useEffect(() => {
        if (!settled) return;
        const fix = wizardReconcile(wizardIn);
        const intentKey = `${wizardIn.intent.withWhatsApp}|${wizardIn.intent.suppressEmail}|`;
        if (lastFix.current != null && lastFix.current.indexOf(intentKey) !== 0) lastFix.current = null;   // the store moved
        const once = (what: string, run: () => void) => {
            const key = `${intentKey}${what}`;
            if (lastFix.current === key) return;
            lastFix.current = key;
            run();
        };
        // A kept WhatsApp intent the account cannot honour now: the existing entry reset (QA BUG-31).
        if (fix.resetWa) once('resetWa', () => dispatch(resetWaForPicker()));
        else if (fix.apply && fix.apply.length) once(JSON.stringify(fix.apply), () => runChannelActions(fix.apply as ChannelAction[]));
        if (fix.chanChosen !== chanChosen) setChanChosen(fix.chanChosen);
        // Every input of `wizardReconcile`, as scalars (the input object is new each render). `wv.active`: nothing is
        // settled while the operator stands on step ①.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settled, withWhatsApp, suppressEmail, guard.waAccount, guard.waReady, guard.emailAlreadySent, guard.noCell,
        guard.hasLeg, wizardIn.src.known, wizardIn.src.hasEmail, channels.email, channels.whatsapp, chanChosen, wv.active]);

    // The step the operator starts on is pinned on the FIRST render the wizard draws — never left to follow the
    // answers. (An entry without ?dataSourceId draws before WhatsApp answered and always starts at ①: the entry
    // reset cleared the slice's source. The URL-source entry draws only once WhatsApp and its row are in, and starts
    // at the first step still open — past an automatic ②.) A step that leaves the bar (its channel went off) is
    // replaced by the one the view stands on, so it can never come back by itself.
    useEffect(() => {
        if (wizardLive && active !== wv.active) setActive(wv.active);
    }, [wizardLive, active, wv.active]);

    // A double click moves one step only: a click within CLICK_GAP_MS of a step change is dropped (`acceptsClick`) —
    // anywhere in the wizard, by a capture-phase guard on its container (so the second click selects nothing on the
    // new step: a campaign, a card, a «שנה»), and again in «המשך» / «המשך למיפוי השדות».
    const stepShownAt = useRef(0);
    useEffect(() => { stepShownAt.current = Date.now(); }, [wv.active]);
    const dropCarriedClick = (e: React.MouseEvent) => {
        if (acceptsClick(stepShownAt.current, Date.now())) return;
        e.stopPropagation();
        e.preventDefault();
    };

    // Moving to another step brings the step bar into view when its top is off screen (r4), and — r3 — moves the focus to the new step's question
    // (a heading, focusable by script only) ONLY when the move came from the keyboard (`arrivalFocus`): the site's
    // accessibility overlay frames any focused element in yellow, which after a mouse click read as a bug. After a
    // pointer the focus stays on the control that was used; if that control is gone or disabled, it is dropped quietly.
    // Nothing is hidden by a choice inside a step, so a menu or a dialog closing hands the focus back to a control
    // that is still on screen.
    const headingRefs = useRef<Partial<Record<WizardStep, HTMLHeadingElement | null>>>({});
    const barRef = useRef<HTMLDivElement | null>(null);
    const shownStep = useRef<WizardStep | null>(null);
    const lastInputKeyboard = useRef(false);
    useEffect(() => {
        const onKey = () => { lastInputKeyboard.current = true; };
        const onPointer = () => { lastInputKeyboard.current = false; };
        document.addEventListener('keydown', onKey, true);
        document.addEventListener('pointerdown', onPointer, true);
        document.addEventListener('mousedown', onPointer, true);
        return () => {
            document.removeEventListener('keydown', onKey, true);
            document.removeEventListener('pointerdown', onPointer, true);
            document.removeEventListener('mousedown', onPointer, true);
        };
    }, []);
    // r4/r4c — the page's vertical scrollbar is always shown while THIS screen is mounted (a class on <html>, removed on
    // leave), so it never comes and goes between steps and moves every centred circle and button sideways. Why a class
    // and not r4b's scrollbar-gutter: see useStableScrollbar (MUI's Modal scroll lock).
    useStableScrollbar();
    // When the question does not take the focus, the new step is still ANNOUNCED (a polite live region): a screen
    // reader's activation of «המשך» arrives as a mouse click, so the focus rule alone would leave it silent.
    const [stepAnnounce, setStepAnnounce] = useState('');
    useEffect(() => {
        if (!wizardLive) return;
        const prev = shownStep.current;
        shownStep.current = wv.active;
        if (prev == null || prev === wv.active) return;
        const el = document.activeElement as HTMLElement | null;
        // Not usable either: an inert step (aria-disabled), or the step just clicked, which is now the current step.
        const usable = !!el && el !== document.body && el.offsetParent != null && !(el as HTMLButtonElement).disabled
            && el.getAttribute('aria-disabled') !== 'true' && el.getAttribute('aria-current') !== 'step';
        const where = arrivalFocus(lastInputKeyboard.current, usable);
        if (where === 'heading') {
            headingRefs.current[wv.active]?.focus({ preventScroll: true });
            setStepAnnounce('');   // the focused question is read; no second announcement (Reviewer N4)
        } else {
            if (where === 'blur' && el && typeof el.blur === 'function') el.blur();
            setStepAnnounce(t(`DataSources.send.picker.wizard.q.${wv.active}`));
        }
        // r4 — scroll only when the bar's top is out of view: a page that already shows it does not jump.
        const bar = barRef.current;
        if (bar && typeof bar.getBoundingClientRect === 'function' && typeof bar.scrollIntoView === 'function') {
            const top = bar.getBoundingClientRect().top;
            if (top < 0 || top > window.innerHeight) bar.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
    }, [wv.active, wizardLive, t]);

    // While Continue is reading the row or creating the WhatsApp unit, nothing on the screen may change what it is
    // about to send: no «חזרה», no bar, no «שנה» (r2).
    const busy = creatingContainer || checkingRow || leavingUnit;
    // Every move to another step: leaving ① settles step ② first, in this same handler (`leaveFix`), so the store, the
    // bar and the step change in ONE render (React 17 batches them) and the step landed on is mounted; and the time of
    // the step change is taken here too, not only in the effect, for the double-click guard.
    const moveTo = (k: WizardStep) => {
        const fix = leaveFix(wizardIn, k);
        if (fix) {
            if (fix.resetWa) dispatch(resetWaForPicker());
            else if (fix.apply && fix.apply.length) runChannelActions(fix.apply);
            if (fix.chanChosen !== chanChosen) setChanChosen(fix.chanChosen);
        }
        stepShownAt.current = Date.now();
        setActive(k);
    };
    // Forward only once WhatsApp has answered (step ② may still appear on the bar); back always — except while
    // Continue is in flight (`busy`).
    const goTo = (k: WizardStep) => {
        if (busy || !wv.steps[k].navigable) return;
        if (!settled && wv.bar.indexOf(k) > wv.bar.indexOf(wv.active)) return;
        moveTo(k);
    };
    const goNext = () => {
        if (busy || !acceptsClick(stepShownAt.current, Date.now())) return;
        if (settled && wv.nextEnabled && wv.next) moveTo(wv.next);
    };
    // «המשך למיפוי השדות» — its own button in the review card, with the same guard against a carried-over click.
    const goFinal = () => {
        if (!acceptsClick(stepShownAt.current, Date.now())) return;
        if (settled && wv.finalEnabled) goToMapping();
    };
    const goBack = () => { if (!busy && wv.back) moveTo(wv.back); };
    const onChooseSet = (set: ChannelSet) => {
        const path = chooseChannels(wizardIn, set);
        if (path == null) return;
        runChannelActions(path);
        setChanChosen(true);
    };

    // ── the words ──
    const W = (key: string, opts?: any) => t(`DataSources.send.picker.wizard.${key}`, opts);
    const setLabel = (s: ChannelSet) => W(`set.${s}`);
    // A source with no name to show (a URL source whose row is still unknown) reads as "the selected source", never «—».
    const sourceName: string | null = pickerSourceRow?.Name || fromSourceName
        || (dataSourceId || pickedSourceId != null ? t('DataSources.send.source.current') : null);
    const campaignName: string | null = campaign?.Name
        ?? (pendingLeg ? pendingLeg.CampaignName : null)
        ?? (seededPickable != null ? (seededRow?.Name ?? null) : null);
    const templateName: string | null = waTemplateCard?.name ?? sv.sections.whatsapp.summaryName ?? null;
    // QA BUG-35 — the joined-unit question: what it shows, and whether «השאר אימייל + וואטסאפ» is possible now.
    const shownUnitInfo: WaUnitSummary | null = shownUnitAsk.current?.info ?? null;
    const unitCanKeep = keepBothPath(wizardIn) != null;
    const manyNumbers = (waNumbers ?? []).length > 1;
    const curSet: ChannelSet | null = channels.email && channels.whatsapp ? 'EW' : (channels.whatsapp ? 'W' : (channels.email ? 'E' : null));
    const reasonText = (r: string | null): string | null => {
        if (r == null) return null;
        if (r === 'waOff') return waBlockReason ? t(`DataSources.send.whatsapp.blocked.${waBlockReason}`) : W('reason.waOff');
        return W(`reason.${r}`);
    };
    // Why a step is «לא נדרש» — the same words as the dimmed card's own reason when ② was automatic.
    const notNeededText = (r: NotNeededReason): string => {
        if (r === 'noWaAccount' || r === 'noEmailChosen' || r === 'noWaChosen') return W(`notNeededWhy.${r}`);
        if (r === 'sourceNoEmail') return W('reason.noEmail');
        if (r === 'sourceNoCell') return W('reason.noCell');
        if (r === 'waNotReady') return reasonText('waOff') ?? W('reason.waOff');
        return W(`reason.${r}`);
    };
    // «המקור קובע»: why only one card is possible. In R2 (a campaign came in the URL) the no-e-mail case names
    // the campaign that will not be sent.
    // WhatsApp not Ready: the note names the server's reason (as the dimmed card does) and keeps the old tile's link to
    // template management — rendered only where WhatsApp is in the account (the route exists for this user).
    const autoNoteText: string | null = (() => {
        if (wv.autoNote == null) return null;
        // The campaign's name is isolated (FSI…PDI) inside the RTL sentence, so "50% off" is not reordered.
        if (wv.autoNote === 'onlyWaNoEmail' && campaignName) return W('note.onlyWaNoEmailCampaign', { name: isolateBidi(campaignName) });
        if (wv.autoNote === 'onlyEmailWaOff' && waBlockReason) {
            return W('note.onlyEmailWaOffReason', { reason: t(`DataSources.send.whatsapp.blocked.${waBlockReason}`) });
        }
        return W(`note.${wv.autoNote}`);
    })();
    const autoNoteAction = wv.autoNote === 'onlyEmailWaOff' && waBlockReason && guard.waAccount
        ? (
            <Link href={whatsappRoutes.TEMPLATE_MANAGEMENT} underline="always">
                {t('DataSources.send.whatsapp.blocked.manageTemplates')}
            </Link>
        )
        : null;
    const countsText = [
        channels.email && emailCount != null ? W('countEmail', { count: emailCount.toLocaleString() }) : null,
        channels.whatsapp && cellCount != null ? W('countWa', { count: cellCount.toLocaleString() }) : null,
    ].filter(Boolean).join(' · ');
    // Names come from the customer's data (underscores, Latin, digits): each is an isolated run (<bdi>) so the RTL
    // line does not reorder it; the sender number is forced left-to-right.
    const summaryParts = [
        sourceName && wv.steps.source.done ? { label: W('step.source'), value: sourceName } : null,
        wv.steps.channels.onBar && wv.steps.channels.done && curSet ? { label: W('step.channels'), value: setLabel(curSet) } : null,
        wv.steps.email.onBar && wv.steps.email.done && campaignName ? { label: W('step.email'), value: campaignName } : null,
        wv.steps.whatsapp.onBar && wv.steps.whatsapp.done && templateName ? { label: W('step.whatsapp'), value: templateName } : null,
    ].filter(Boolean) as { label: string; value: string }[];
    const summaryLine = summaryParts.length
        ? summaryParts.map((p, n) => (
            <React.Fragment key={p.label}>
                {n > 0 ? ' · ' : null}
                {`${p.label}: `}
                <bdi>{p.value}</bdi>
            </React.Fragment>
        ))
        : null;
    const changeTo = (k: WizardStep) => (wv.steps[k].onBar && !wv.steps[k].locked && !wv.steps[k].auto ? k : null);
    const reviewRows: ReviewRowView[] = [
        { key: 'source', label: W('step.source'), value: sourceName ?? '—', changeTo: changeTo('source') },
        { key: 'channels', label: W('step.channels'), value: curSet ? setLabel(curSet) : '—', extra: countsText || null, changeTo: changeTo('channels') },
        ...(channels.email ? [{ key: 'email', label: W('step.email'), value: campaignName ?? '—', changeTo: changeTo('email') }] : []),
        ...(channels.whatsapp ? [{ key: 'whatsapp', label: W('step.whatsapp'), value: templateName ?? '—', changeTo: changeTo('whatsapp') }] : []),
        ...(channels.whatsapp && manyNumbers
            ? [{ key: 'number', label: W('fromNumber'), value: waFromNumber ?? '—', ltr: true, changeTo: changeTo('whatsapp') }]
            : []),
    ];
    // «המקור קובע» is shown where the operator lands: on ① once a source is picked, on ② itself, on ⑤ — and, when ②
    // is automatic (passed over by «המשך», and skipped by the URL-source entry), on the first step after it.
    const noteAfterAuto: WizardStep | null = wv.steps.channels.auto
        ? (wv.bar[wv.bar.indexOf('channels') + 1] ?? null)
        : null;
    const showNoteOn = (k: WizardStep) => !!autoNoteText && (k === 'channels' || k === 'review'
        || (k === 'source' && wv.steps.source.done) || k === noteAfterAuto);
    // No card possible: the way out — another source (this screen without one), or the sources list.
    const noneActions = wv.autoNote === 'none'
        ? (
            <>
                <Link component="button" underline="always" onClick={() => routerNavigate(`${sitePrefix}SmartSend`)}>
                    {W('pickOtherSource')}
                </Link>
                {' · '}
                <Link href={`${sitePrefix}DataSources`} underline="always">{W('sourcesList')}</Link>
            </>
        )
        : null;
    const noteFor = (k: WizardStep) => (showNoteOn(k)
        ? <WizardNote text={autoNoteText as string} action={autoNoteAction ?? noneActions} />
        : null);
    // ④ — the template list failed to load: say so, and offer to load it again (the grid itself shows nothing then).
    const templatesFailed = channels.whatsapp && waTemplatesStatus === 'failed';
    // With two or more connected numbers, step ④ asks for the sender number too (its ✓ needs one) — the hint says so.
    const stepHint = (k: WizardStep) => W(k === 'whatsapp' && manyNumbers ? 'hint.whatsappMany' : `hint.${k}`);
    const headingRef = (k: WizardStep) => (el: HTMLHeadingElement | null) => { headingRefs.current[k] = el; };

    // QA BUG-29 · R2 — while the seeded campaign's row is being read, nothing to choose yet: a mapped
    // campaign leaves for its mapping page the moment the answer lands.
    // QA BUG-35 (Idan, approved (א)) — the same skeleton as the wizard's own wait, so an unmapped campaign's wizard
    // appears in place (the bar, the title's margin) instead of replacing a bare spinner.
    if (seedChecking) {
        return (
            <DefaultScreen currentPage="groups" subPage="smartSend" classes={classes} containerClass={clsx(classes.management, classes.mb50)}>
                <Box style={{ padding: 16 }}>
                    <Typography variant="h5" style={{ marginBottom: 16 }}>{t('DataSources.send.picker.title')}</Typography>
                    <WizardSkeleton label={(k) => W(`step.${k}`)} ariaLabel={W('barAria')} />
                </Box>
            </DefaultScreen>
        );
    }

    // FU-06A — the same wait while the slice is reset for this URL and WhatsApp's own answer (and, on the URL-source
    // entry, the source's row) comes in: the bar's five steps and a loading card, not an empty page (r2).
    if (!wizardLive) {
        return (
            <DefaultScreen currentPage="groups" subPage="smartSend" classes={classes} containerClass={clsx(classes.management, classes.mb50)}>
                <Box style={{ padding: 16 }}>
                    <Typography variant="h5" style={{ marginBottom: 16 }}>{t('DataSources.send.picker.title')}</Typography>
                    <WizardSkeleton label={(k) => W(`step.${k}`)} ariaLabel={W('barAria')} />
                </Box>
            </DefaultScreen>
        );
    }

    return (
        <DefaultScreen currentPage="groups" subPage="smartSend" classes={classes} containerClass={clsx(classes.management, classes.mb50)}>
            {/* r2/r3 — bottom room for the floating Pulsi bubbles, which sit in BOTH bottom corners: the wizard's buttons
                are centred (WizardNav), and 160px below them lets the page scroll them above the bubbles whenever the
                content reaches the bottom of a short screen. The widgets themselves are not touched. */}
            <Box style={{ padding: 16, paddingBottom: 160 }} onClickCapture={dropCarriedClick}>
                <Typography variant="h5" style={{ marginBottom: 16 }}>{t('DataSources.send.picker.title')}</Typography>
                {/* r3 — the new step's question, announced when it does not take the focus (visually hidden). */}
                <span
                    role="status"
                    aria-live="polite"
                    style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0 }}
                >
                    {stepAnnounce}
                </span>

                {/* FU-06A — the step bar: all five steps, always numbered 1..5 (r2); a step this send does not have
                    is grey «לא נדרש»; ✓ = answered (and clickable back); the URL's source is ✓ and locked (Idan R1).
                    Under it, what has been chosen so far. */}
                <div ref={barRef}>
                    <WizardStepper
                        items={wv.items}
                        active={wv.active}
                        label={(k) => W(`step.${k}`)}
                        secondary={(k) => (k === 'channels' && curSet ? setLabel(curSet) : null)}
                        notNeededLabel={W('notNeeded')}
                        notNeededTip={notNeededText}
                        pendingLabel={W('dependsOnChannels')}
                        ariaLabel={W('barAria')}
                        lockedTip={W('lockedSource')}
                        summary={summaryLine}
                        summaryTitle={summaryParts.map((p) => `${p.label}: ${isolateBidi(p.value)}`).join(' · ') || null}
                        onJump={goTo}
                        frozen={busy}
                    />
                </div>

                {/* ① מאיזה מקור שולחים? — 🔴 2026-09-23 · ONLY WHEN THE DOOR DID NOT BRING ONE (one road in: the
                    question is asked in ONE place; `navigate` reads the answer back out of the slice).
                    QA BUG-29 — `scopeClean`: mounted only once the slice was reset for this URL. The condition is
                    unchanged; FU-06A only hides it while another step is active. Its cards are gated on what the
                    account can send and say which channels each source serves (`wizardGate`). */}
                {!dataSourceId && scopeClean && (
                    <StepCard question={W('q.source')} hint={stepHint('source')} headingRef={headingRef('source')} hidden={wv.active !== 'source'}>
                        {/* QA BUG-35 (Idan, approved (ב)) — «המקור קובע» ABOVE the source list, by the question: below the
                            grid it pushed «המשך» down when a source was picked. */}
                        {noteFor('source')}
                        <SourcePicker wizardGate={gateChannels} />
                    </StepCard>
                )}

                {/* ② איך שולחים? — only where the account has WhatsApp (without it the send is e-mail and
                    there is nothing to ask). Three cards; a dimmed one says why. A click moves the store only
                    through the old channel row's guards (`chooseChannels` → `runChannelActions`); only one
                    card possible ⇒ it is chosen by itself, and the note says why («המקור קובע»). */}
                {wv.steps.channels.onBar && (
                    <StepCard question={W('q.channels')} hint={stepHint('channels')} headingRef={headingRef('channels')} hidden={wv.active !== 'channels'}>
                        <ChannelCards
                            cards={wv.options.map((o) => ({ set: o.set, label: setLabel(o.set), reasonText: reasonText(o.reason) }))}
                            chosen={wv.chosenSet}
                            ariaLabel={W('q.channels')}
                            onChoose={onChooseSet}
                        />
                        {noteFor('channels')}
                    </StepCard>
                )}

                {/* 🔴🔴 THE CAMPAIGN GRID IS THE E-MAIL CHANNEL'S CONTENT BLOCK, AND IT IS GATED
                    ON THE E-MAIL CHANNEL. 77 §3 cancels 70 (4.4).

                    What used to stand here said "the campaign is the container of EVERY send",
                    so the grid rendered whatever the channel mix was. That is the sentence 48 §1
                    line 4 forbids in as many words — «המכל לעולם אינו קמפיין אימייל» — and it is
                    why a WhatsApp-only send still made the operator pick an e-mail campaign.
                    Under 77 §2 every channel owns its own container:
                        1 → dbo.Campaigns · 2 → dbo.SMSCampaign · 3 → dbo.WhatsAppCampaign
                    so this grid belongs to channel 1 and to nothing else.

                    ⚠️ NOT RENDERED — not hidden, not disabled, not greyed. 48 §2 rule 1. An
                    element that is merely hidden still holds its selection, still autosaves and
                    still answers the "continue" gate; the operator switched the channel off and
                    the block has to stop existing.
                    🔵 Switching e-mail back on re-renders it with the previous pick intact —
                    `campaign` is local screen state and survives the toggle (48 §2 rule 3).
                    FU-06A — step ③ «לאיזה קמפיין אימייל?»: the same condition (`channels.email`), hidden while
                    another step is active. */}
                {channels.email && (
                    <StepCard question={W('q.email')} hint={stepHint('email')} headingRef={headingRef('email')} hidden={wv.active !== 'email'}>
                        {noteFor('email')}
                        {/* A campaign that came in the URL and cannot be sent from a source is SAID (also in ⑤). */}
                        {seedRefusedText && (
                            <Box style={{ margin: '0 0 8px' }}>
                                <InlineBanner severity="warning" role="status" title={seedRefusedText} />
                            </Box>
                        )}
                        <CampaignPicker
                            // 🔴 2026-09-23 — `?? seededCampaignId`, so a campaign handed over in
                            // the URL renders SELECTED. Without it "המשך" opened (selectedId:255
                            // already honours the seed) over a grid with nothing highlighted.
                            // Still `null` while a pending leg is chosen: that is a different
                            // list below, and the two selections are mutually exclusive.
                            value={campaign?.CampaignID ?? (pendingLeg ? null : seededPickable)}
                            onChange={onPickCampaign}
                            pendingLegs={waAllowed && waEnabled ? waPendingLegs : []}
                            onPickPending={onPickPending}
                            selectedPendingId={pendingLeg?.CampaignID ?? null}
                        />
                    </StepCard>
                )}

                {/* The approved template. `channels.whatsapp` is exactly the picker's own `active`
                    gate, so not mounting it here is what it already did to itself.
                    C-44 · the SELECTED campaign's leg, guarded by `mappingCampaignId` — the
                    picker used to read it raw and could be locked by the previous campaign's.
                    FU-06A — step ④ «איזו תבנית וואטסאפ?»: the same condition, hidden while another step is
                    active. The sender number (2+ connected) is chosen here too; nothing hides on a choice, so
                    the number menu closing hands the focus back to its own (visible) field. */}
                {channels.whatsapp && (
                    <StepCard question={W('q.whatsapp')} hint={stepHint('whatsapp')} headingRef={headingRef('whatsapp')} hidden={wv.active !== 'whatsapp'}>

                        {/* 🔴 MOUNTED WHENEVER THE WHATSAPP CHANNEL IS ON — BOTH STATES.
                            The comment that stood here said "mounted only when the e-mail is off"
                            and described a container picker. Neither was true any more: the gate is
                            `channels.whatsapp`, and the container picker was removed on 21.09. A
                            comment that contradicts its own code is how this wave lost a week.

                            09-channel-units — and the template is FOR the same thing in both states:
                            `goToMapping` creates the WhatsApp unit — a dbo.WhatsAppCampaign container
                            FROM the template, with its own mapping row — whether the e-mail is on or
                            off. With the e-mail on, the unit goes beside the e-mail campaign
                            (`?wa={unit}`); it never hangs on it as a leg. */}
                        {/* 🔴 THE TEMPLATE IS THE CHOICE ON THIS CHANNEL — IN BOTH STATES.
                            Owner, 21.09: «באימייל בוחרים קמפיין אימייל, בוטס אפ תבנית שיוצרים
                            ממנה קמפיין». The two channels are not symmetric and were never meant
                            to be: dbo.Campaigns rows are authored by the customer and picked from
                            a list, while a dbo.WhatsAppCampaign row is DERIVED from a template.
                            ⚠️ A WhatsApp-campaign picker was built here and REMOVED the same day:
                            it asked the operator to choose a container, which is an internal
                            artefact she never thinks about. The container is created FROM the
                            template — see `goToMapping`. */}
                        {noteFor('whatsapp')}
                        {templatesFailed && (
                            <Box style={{ marginTop: 8 }}>
                                <InlineBanner
                                    severity="error"
                                    role="alert"
                                    title={W('templatesFailed')}
                                    action={(
                                        <Button size="small" variant="outlined" color="primary" onClick={() => dispatch(getWaTemplates())}>
                                            {t('DataSources.retry')}
                                        </Button>
                                    )}
                                />
                            </Box>
                        )}
                        <WhatsAppTemplatePicker classes={classes} leg={legForSelection} />
                    </StepCard>
                )}

                {/* ⑤ בדיקה לפני המיפוי — what is about to be sent, one row per item with its «שנה», the
                    recipients per lit channel, and EVERY warning about the chosen items in one place. */}
                {wv.active === 'review' && (
                    <StepCard question={W('q.review')} hint={stepHint('review')} headingRef={headingRef('review')}>
                        <ReviewList
                            rows={reviewRows}
                            changeLabel={W('change')}
                            changeAria={(label) => W('changeAria', { label })}
                            onChange={goTo}
                            disabled={busy}
                        />
                        {noteFor('review')}
                        {warnings.length > 0 && (
                            <Box style={{ marginTop: 16 }}>
                                {warnings.map((w) => (
                                    <InlineBanner
                                        key={w}
                                        severity="warning"
                                        role="status"
                                        title={w === 'seedRefused' ? (seedRefusedText ?? '') : t(REVIEW_WARNING_KEY[w])}
                                    />
                                ))}
                            </Box>
                        )}
                        {/* Today's «המשך למיפוי השדות»: the same handler (`goToMapping`) and the same gate
                            (`continueBlockedOf`, inside `finalEnabled`) — its OWN button, in this card, so the bar's
                            «המשך» is never under the pointer as this one (a double click moves one step: `goFinal`).
                            The click makes a network call before it navigates, so it shows its in-flight state
                            (QA BUG-29: the R1 read too). */}
                        <Box style={{ display: 'flex', justifyContent: 'center', marginTop: 24 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                disabled={!wv.finalEnabled || !settled || busy}
                                aria-busy={busy ? true : undefined}
                                onClick={goFinal}
                            >
                                <BusyLabel busy={busy} label={t('DataSources.send.picker.continue')} />
                            </Button>
                        </Box>
                        {/* 🔴 InlineBanner role="alert", like the other refusal surfaces on this path: on a
                            failure the button simply re-enables and nothing else would be announced. */}
                        {createError && (
                            <Box style={{ marginTop: 12 }}>
                                <InlineBanner severity="error" role="alert" title={createError} />
                            </Box>
                        )}
                    </StepCard>
                )}

                {/* «חזרה» (outlined) and «המשך», centred side by side (r3: away from the corner bubbles). On ⑤ only
                    «חזרה»: the final button is in the review card. */}
                <WizardNav
                    backLabel={W('back')}
                    onBack={wv.back ? goBack : null}
                    backDisabled={busy}
                    primaryLabel={W('next')}
                    onPrimary={wv.active === 'review' ? null : goNext}
                    primaryDisabled={!wv.nextEnabled || !settled || busy}
                    busy={wv.nextEnabled && !settled}
                />
            </Box>

            {/* QA BUG-29 · Idan R1 — the campaign's send is mapped to another source. «המשך» arms this
                campaign's confirmation with the TARGET source and continues; the mapping page resets on
                entry. «ביטול» / Esc / backdrop stay here. No autoFocus on «המשך»: a reset is a deliberate
                second click. Same skeleton and mandatory `dir` as the mapping page's reset dialog. */}
            <Dialog open={switchAsk != null} onClose={() => setSwitchAsk(null)} maxWidth="xs" fullWidth dir={isRTL ? 'rtl' : 'ltr'}>
                <Box style={{ padding: '16px 24px', borderBottom: '1px solid var(--pls-d-border, #e0e0e0)' }}>
                    <Typography variant="h6">{t('DataSources.send.source.switchConfirmTitle')}</Typography>
                </Box>
                <Box style={{ padding: 24 }}>
                    <Typography variant="body1">
                        {/* FU-06A r2 — the two source names isolated (FSI…PDI) inside the RTL sentence. */}
                        {t('DataSources.send.source.entryResetBody', {
                            from: isolateBidi(shownAsk.current?.from ?? ''),
                            to: isolateBidi(shownAsk.current?.to ?? ''),
                        })}
                    </Typography>
                </Box>
                <Box style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 24px', borderTop: '1px solid var(--pls-d-border, #e0e0e0)' }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            const ask = switchAsk;
                            setSwitchAsk(null);
                            if (!ask) return;
                            // FU-06A r2 — the screen must still be the one the question was asked about: the same
                            // campaign and source, and today's gate still open. Otherwise nothing is armed or sent.
                            if (!wv.finalEnabled || ask.campaignId !== selectedId
                                || ask.target !== Number(dataSourceId || pickedSourceId)) return;
                            armConfirmReset(eSendChannel.EMAIL, ask.campaignId, ask.target);
                            // QA BUG-35 — the send's row also holds a WhatsApp unit and ② is «אימייל»: that question
                            // next (I8); the reset confirmation armed above waits for whichever page opens.
                            if (ask.unit) openUnitAsk(ask.unit);
                            else proceedToMapping();
                        }}
                    >
                        {t('DataSources.send.source.entryResetContinue')}
                    </Button>
                    <Box style={{ flex: 1 }} />
                    <Button onClick={() => setSwitchAsk(null)}>{t('DataSources.send.cancel')}</Button>
                </Box>
            </Dialog>

            {/* QA BUG-35 · I8 — ② is «אימייל» and the campaign's send already holds a WhatsApp unit (an earlier «אימייל +
                וואטסאפ»), which «שלח לכולם» would send with it. Three answers: «הסר» (POST Leave, then the e-mail-only
                page), «השאר» (② becomes E+W through the guards; offered only when possible), «ביטול». No autoFocus. */}
            <Dialog open={unitAsk != null} onClose={() => setUnitAsk(null)} maxWidth="sm" fullWidth dir={isRTL ? 'rtl' : 'ltr'}>
                <Box style={{ padding: '16px 24px', borderBottom: '1px solid var(--pls-d-border, #e0e0e0)' }}>
                    <Typography variant="h6">{W('unitAsk.title')}</Typography>
                </Box>
                <Box style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <Typography variant="body1">
                        {/* Names from the customer's data, isolated (FSI…PDI) inside the RTL sentence. */}
                        {shownUnitInfo?.templateName
                            ? W('unitAsk.body', {
                                campaign: isolateBidi(campaignName ?? `#${shownUnitAsk.current?.campaignId ?? ''}`),
                                template: isolateBidi(shownUnitInfo.templateName),
                                mapped: shownUnitInfo.mapped,
                                total: shownUnitInfo.total,
                            })
                            : W('unitAsk.bodyNoDetails', {
                                campaign: isolateBidi(campaignName ?? `#${shownUnitAsk.current?.campaignId ?? ''}`),
                            })}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">{W(unitCanKeep ? 'unitAsk.hint' : 'unitAsk.hintNoKeep')}</Typography>
                </Box>
                <Box style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12, padding: '16px 24px', borderTop: '1px solid var(--pls-d-border, #e0e0e0)' }}>
                    <Button variant="contained" color="primary" onClick={onUnitRemove}>{W('unitAsk.remove')}</Button>
                    {unitCanKeep && (
                        <Button variant="outlined" color="primary" onClick={onUnitKeep}>{W('unitAsk.keep')}</Button>
                    )}
                    <Box style={{ flex: 1 }} />
                    <Button onClick={() => setUnitAsk(null)}>{t('DataSources.send.cancel')}</Button>
                </Box>
            </Dialog>
        </DefaultScreen>
    );
};

export default SmartSendPicker;
